import { useState } from 'react';
import { useSystemAuthDispatch, useSystemAuthState } from 'auth/SystemAuth/context/context';
import { useVendorLogout } from 'auth/VendorAuth/hooks';
import { VendorUserType } from 'auth/VendorAuth/vendorUserSlice';
import axios, { AxiosError } from 'axios';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import useConfirmModal from 'hooks/useConfirmModal';
import { useNavigate } from 'react-router-dom';
import { ConfirmModalProps } from 'store/confirmModalSlice';
import { showErrorMessage } from 'store/errorModalSlice';
import { selectRfi } from 'store/rfiSlice';
import { clearVendorResponseState, selectVendorResponse } from 'store/vendorResponseSlice';
import RfiElement from 'types/RfiElement';
import { RfiStatus } from 'types/RfiTypes';
import { Tooltip } from '@mui/material';
import { Loader } from '@progress/kendo-react-indicators';
import VendorRfiHistoryModal from './VendorRfiHistoryModal';

interface IRfiHeaderProps {
  user?: VendorUserType;
  readOnly?: boolean;
  onResponseDeleted?: () => void;
}

const VendorRfiNavBar = (props: IRfiHeaderProps) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(localizedFormat);
  dayjs.extend(advancedFormat);

  // Hooks
  const authState = useSystemAuthState();
  const rfi = useAppSelector(selectRfi);
  const vendorResponse = useAppSelector(selectVendorResponse);
  const authDispatch = useSystemAuthDispatch();
  const navigate = useNavigate();
  const { open } = useConfirmModal();
  const dispatch = useAppDispatch();
  const { vendorLogOut } = useVendorLogout();

  const rfiArchived =
    rfi.rfi?.status === RfiStatus.POSTED &&
    rfi.rfi?.archiveDate &&
    dayjs.utc() > dayjs.utc(rfi.rfi?.archiveDate).add(1, "day")
      ? true
      : false;

  const timeZoneElement = rfi.finalize?.find((e: RfiElement) => {
    return e.mapsToField === "RESPONSES_DUE_TZ";
  });

  const timeZone =
    timeZoneElement &&
    timeZoneElement.answers &&
    timeZoneElement.answers[0]?.value
      ? timeZoneElement.answers[0]?.value
      : undefined;

  // State

  const [isPrinting, setIsPrinting] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Handlers
  const downloadRfiPdf = () => {
    setIsPrinting(true);
    axios
      .get(`/api/v1/rfi/print/${rfi.rfi?.rfiId}`, { responseType: "blob" })
      .then((response) => {
        let url = URL.createObjectURL(response.data);
        let a = document.createElement("a");
        a.href = url;
        a.download = `${rfi.rfi?.rfiNumber}_${rfi.rfi?.versionNumber}.pdf`;
        a.rel = "noopener";
        a.click();
        setIsPrinting(false);
      });
  };

  const downloadRfiResponsePdf = () => {
    setIsPrinting(true);
    axios
      .get(
        `/api/v1/rfi/print/${rfi.rfi?.rfiId}/response/${vendorResponse.vendorResponse?.vendorResponseId}`,
        { responseType: "blob" }
      )
      .then((response) => {
        let url = URL.createObjectURL(response.data);
        let a = document.createElement("a");
        a.href = url;
        a.download = `Response_${rfi.rfi?.rfiNumber}_${rfi.rfi?.versionNumber}.pdf`;
        a.rel = "noopener";
        a.click();
        setIsPrinting(false);
      });
  };

  const handleHistoryClicked = () => {
    setShowHistoryModal(true);
  };
  let closeDateString = "";
  let daysRemaining = 0;
  if (rfi.rfi?.qaCloseDate) {
    const dateString = dayjs
      .utc(rfi.rfi?.qaCloseDate)
      .format("YYYY-MM-DDTHH:mm:ss");
    const date = dayjs.tz(dateString, timeZone ?? "America/New_York");

    closeDateString = date.format("lll z");
    const closeDate = dayjs(rfi.rfi?.qaCloseDate);
    daysRemaining = closeDate.diff(dayjs(), "day") + 1;
    daysRemaining = daysRemaining >= 0 ? daysRemaining : 0;
  }

  let responseDateString = "";
  let responseDaysRemaining = 0;

  if (rfi.rfi?.responsesDueDate) {
    const dateString = dayjs
      .utc(rfi.rfi?.responsesDueDate)
      .format("YYYY-MM-DDTHH:mm:ss");
    const date = dayjs.tz(dateString, timeZone ?? "America/New_York");

    responseDateString = date.format("lll z");
    const responseDate = dayjs(rfi.rfi?.responsesDueDate);
    responseDaysRemaining = responseDate.diff(dayjs(), "day") + 1;
    responseDaysRemaining =
      responseDaysRemaining >= 0 ? responseDaysRemaining : 0;
  }

  const onDelete = async () => {
    const message = `If you delete your response to ${rfi.rfi?.rfiNumber}, you will not receive notifications when this RFI is amended or cancelled, and you will be logged out.  Would you like to continue? `;

    const isConfirmed = await open({
      title: "Confirm Delete",
      message: message,
      description: "Confirm delete RFI",
      confirmText: "Yes",
      cancelText: "No",
    } as ConfirmModalProps);

    if (isConfirmed) {
      await axios
        .delete(
          `/api/v1/rfi/${rfi.rfi?.rfiId}/vendorresponses/${vendorResponse?.vendorResponse?.vendorResponseId}`
        )
        .then(() => {
          vendorLogOut();
          dispatch(clearVendorResponseState());
          props.onResponseDeleted && props.onResponseDeleted();
        })
        .catch((error: Error | AxiosError) => {
          console.log(error);
          dispatch(
            showErrorMessage(
              `There was an error deleting this Response - ${error}`
            )
          );
        });
    }
  };

  // Render
  return (
    <>
      <nav
        id="rfiNav"
        className="navbar navbar-expand-lg navbar-light bg-light mb-3 justify-content-between"
      >
        <div>
          {rfi.rfi?.title.length && rfi.rfi?.title?.length <= 100 && (
            <h1 className="h3 mb-0" id="rfiName">
              {rfi.rfi?.title}
            </h1>
          )}
          {rfi.rfi?.title?.length && rfi.rfi?.title?.length > 100 && (
            <Tooltip title={rfi.rfi?.title} placement="top">
              <h1 className="h3 mb-0" id="rfiName" aria-label={rfi.rfi?.title}>
                {`${rfi.rfi?.title.substring(0, 100)}…`}{" "}
              </h1>
            </Tooltip>
          )}
          <strong>
            RFI: <span id="rfiNumber"> {rfi.rfi?.rfiNumber}</span>{" "}
            <span id="rfiStatus">
              {rfi.rfi?.versionNumber === 0
                ? "BASE"
                : `Amendment: ${rfi.rfi?.versionNumber}`}{" "}
              {rfi.rfi?.postedToSamDate &&
                `${
                  rfi.rfi?.versionNumber > 0 ? "Updated" : RfiStatus.POSTED
                }: ${dayjs
                  .utc(rfi.rfi?.postedToSamDate)
                  .tz(timeZone ?? "America/New_York")
                  .format("lll")}`}
            </span>
          </strong>
          {props.user &&
          props.user.isAuthenticated &&
          vendorResponse.vendorResponse?.dateSubmitted ? (
            <strong className="k-ml-3">/ Response Submitted</strong>
          ) : props.user &&
            props.user.isAuthenticated &&
            (vendorResponse.vendorResponse?.vendorResponseId ?? 0) > 0 &&
            rfi.rfi?.status !== RfiStatus.CANCELLED &&
            !rfiArchived ? (
            <strong className="k-ml-3">/ Response In Progress</strong>
          ) : (
            ""
          )}
        </div>
        <div>
          {rfi.rfi?.status === RfiStatus.CANCELLED && (
            <strong className="h3">RFI Cancelled</strong>
          )}
          {rfi.rfi?.status === RfiStatus.POSTED && rfiArchived && (
            <strong className="h3">RFI Archived</strong>
          )}
          {closeDateString &&
            rfi.rfi?.status === RfiStatus.POSTED &&
            !rfiArchived && (
              <div>
                <strong>{`Q&A ${
                  daysRemaining === 0 ? "Closed:" : "Closes:"
                }`}</strong>{" "}
                <span id="qaCloseDateTime">{closeDateString}</span>
              </div>
            )}
          {responseDateString &&
            rfi.rfi?.status === RfiStatus.POSTED &&
            !rfiArchived && (
              <div>
                <strong>Responses Due:</strong>{" "}
                <span id="responseDueDateTime">{responseDateString}</span>
              </div>
            )}
        </div>
        <div id="navbarNav" className="d-lg-block">
          <ul className="navbar-nav ml-auto mt-3 flex-row">
            {/* TODO: Moved to 1.1 release */}
            {/* <li className="nav-item">
            {isPrinting && <Loader type="infinite-spinner" />}
            {!isPrinting && 
              <a className="nav-link" href="#" onClick = {downloadRfiPdf} >
                <i className="fa-solid fa-print fa-xl d-block mb-2 text-center"></i>print response</a>
                }
            </li> */}
            {((props.user && !props.user.isAuthenticated) ||
              (vendorResponse.vendorResponse?.vendorResponseId ?? 0) === 0) && (
              <li className="nav-item">
                {isPrinting && <Loader type="infinite-spinner" />}
                {!isPrinting && (
                  <a className="nav-link" href="#" onClick={downloadRfiPdf}>
                    <i className="fa-solid fa-print fa-xl d-block mb-2 text-center"></i>
                    print
                  </a>
                )}
              </li>
            )}
            {props.user &&
              props.user &&
              props.user.isAuthenticated &&
              (vendorResponse.vendorResponse?.vendorResponseId ?? 0) > 0 && (
                <li className="nav-item">
                  {isPrinting && <Loader type="infinite-spinner" />}
                  {!isPrinting && (
                    <a
                      className="nav-link"
                      href="#"
                      onClick={downloadRfiResponsePdf}
                    >
                      <i className="fa-solid fa-print fa-xl d-block mb-2 text-center"></i>
                      print response
                    </a>
                  )}
                </li>
              )}
            <li className="nav-item">
              <a className="nav-link" href="#" onClick={handleHistoryClicked}>
                <i className="fa-solid fa-clock-rotate-left fa-xl d-block mb-2 text-center"></i>
                history
              </a>
            </li>
            {props.user &&
              props.user.isAuthenticated &&
              (vendorResponse.vendorResponse?.vendorResponseId ?? 0) > 0 &&
              !vendorResponse.vendorResponse?.dateSubmitted && (
                <li className="nav-item">
                  <button className="nav-link" onClick={onDelete}>
                    <i className="fa-solid fa-trash fa-xl d-block mb-2 text-center"></i>
                    delete
                  </button>
                </li>
              )}
          </ul>
        </div>
      </nav>
      <VendorRfiHistoryModal
        show={showHistoryModal}
        handleClose={() => setShowHistoryModal(false)}
      />
    </>
  );
};

export default VendorRfiNavBar;
